import React from "react";
import Image from "next/image";
import { useImageDimensions } from "~/hooks/use-image-dimensions";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "components/ui/carousel";
import { Dialog, DialogContent, DialogTrigger } from "components/ui/dialog";
import { AspectRatio } from "~/components/ui/aspect-ratio";

type ImageCarouselProps = {
  images: { url: string; caption: string }[];
};

export function ImageCarousel({ images }: ImageCarouselProps) {
  const { dimensions, loading } = useImageDimensions(
    images.map((image) => image.url),
  );

  if (loading) {
    return (
      <div className="flex h-96 w-full animate-pulse items-center justify-center rounded-lg bg-gray-200">
        <span className="text-gray-500">Loading images...</span>
      </div>
    );
  }

  return (
    <Carousel className="w-full py-3" opts={{ loop: true }}>
      <CarouselContent>
        {images.map((image, index) => {
          const imageDimensions = dimensions[image.url];
          const aspectRatio = imageDimensions?.aspectRatio || 16 / 9;

          return (
            <Dialog key={index}>
              <CarouselItem>
                <div className="w-full">
                  <div className="w-full overflow-hidden rounded-lg">
                    <AspectRatio ratio={aspectRatio}>
                      <DialogTrigger>
                        <Image
                          src={image.url}
                          alt={`Image ${index + 1}`}
                          fill
                          className="not-prose"
                          unoptimized
                          objectFit="cover"
                        />
                      </DialogTrigger>
                    </AspectRatio>
                  </div>
                  <p className="mt-2 text-center text-sm text-gray-600">
                    {image.caption}
                  </p>
                </div>
                <DialogContent className="w-full max-w-none md:w-10/12 xl:max-w-7xl">
                  <AspectRatio ratio={aspectRatio}>
                    <Image
                      src={image.url}
                      alt={`Image ${index + 1}`}
                      fill
                      className="not-prose"
                      unoptimized
                      objectFit="cover"
                    />
                  </AspectRatio>
                  {image.caption !== "" && (
                    <p className="mt-2 text-center text-sm text-gray-600">
                      {image.caption}
                    </p>
                  )}
                </DialogContent>
              </CarouselItem>
            </Dialog>
          );
        })}
      </CarouselContent>
      <CarouselPrevious className=" hover:bg-slate-900/10" />
      <CarouselNext className=" hover:bg-slate-900/10" />
    </Carousel>
  );
}
